/**
 * Job-Description extraction schema (Pool smart search, BL-207).
 *
 * The LLM turns a pasted Job Description (text or fetched URL) into structured
 * search criteria: a hard pre-filter (skills / seniority / location) plus a
 * compact `canonicalText` restatement of the ideal candidate that we EMBED for
 * semantic ranking against the pool. Every field is nullable / defaulted so the
 * AI never fabricates — a missing section means "not specified".
 */

import { z } from 'zod';

export const jobDescriptionCriteriaSchema = z.object({
  /** The role/title the JD targets (normalized, e.g. "Senior Backend Engineer"). */
  title: z.string().nullable().default(null),
  /** Seniority band inferred from the JD (e.g. "senior", "mid", "lead"). */
  seniority: z.string().nullable().default(null),
  /** Required/expected skills, normalized to short tokens (e.g. "React", "Node"). */
  skills: z.array(z.string()).default([]),
  /** Hard requirements — the candidate must satisfy these. */
  mustHaves: z.array(z.string()).default([]),
  /** Preferred / nice-to-have signals. */
  niceToHaves: z.array(z.string()).default([]),
  /** Geography the role is tied to (city / country / "remote"), if stated. */
  location: z.string().nullable().default(null),
  /**
   * A compact, single-paragraph restatement of the ideal candidate — the text
   * we embed for semantic (cosine) ranking against each pool candidate's
   * `poolSourceText`. Always present.
   */
  canonicalText: z.string(),
});

export type JobDescriptionCriteria = z.infer<typeof jobDescriptionCriteriaSchema>;
